import React, { useCallback, useState, Fragment, useEffect } from 'react'
import { ThemeProvider } from 'styled-components'

import { KEY_VALUES } from '../../constants'
import { useTranslation } from '../../contexts/I18nContext'
import { overrideIntercomLanguage } from '../../helpers'
import Icon from '../Icon'
import { COLORS, THEME } from '../../styles'

import Styled from './LocaleSwitcher.styled'

const AVAILABLE_LANGUAGES = {
  fr: 'Français',
  en: 'English',
}
const AVAILABLE_FLAGS = {
  fr: '🇫🇷',
  en: '🇺🇸',
}

const LocaleSwitcher = ({
  className,
  getConfig,
  updateDevice,
  variant,
  path,
}) => {
  const { locale, supportedLanguages = [] } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    overrideIntercomLanguage(locale)
  }, [locale])

  const changeLocale = useCallback(
    (lang) => () => {
      if (lang !== locale) {
        updateDevice({ language: lang })
        getConfig({
          availabilityZoneId: process.env.NEXT_PUBLIC_AVAILABILITY_ZONE,
        })
      }
    },
    [locale, getConfig, updateDevice],
  )

  return (
    <ThemeProvider theme={{ variant }}>
      <Styled className={className}>
        <Styled.tooltip
          isOpen={isOpen}
          onClick={() => setIsOpen((o) => !o)}
          role="button"
          tabIndex={0}
          onKeyPress={(event) => {
            if (event.key === KEY_VALUES.ENTER) {
              setIsOpen((o) => !o)
            }
          }}
          onMouseLeave={() => setIsOpen(false)}
          onMouseEnter={() => setIsOpen(true)}
        >
          <Styled.tooltipDiscoverable>
            <Icon
              name="language-16"
              fill={
                variant === 'page'
                  ? THEME.COLORS.foreground.primary
                  : COLORS.primary
              }
              width={16}
              height={16}
            />
            <Styled.tooltipDiscoverableLanguage>
              {AVAILABLE_LANGUAGES[locale]}
            </Styled.tooltipDiscoverableLanguage>
            <Styled.tooltipDiscoverableDropdown
              name="direction-down-16"
              width={16}
              height={16}
              fill={COLORS.black}
              isOpen={isOpen}
            />
          </Styled.tooltipDiscoverable>
          <Styled.tootipContainer>
            <Styled.tooltipContent>
              {supportedLanguages.map((lang, index) => {
                const isChecked = lang === locale
                return (
                  <Fragment key={lang}>
                    <Styled.language
                      onClick={changeLocale(lang)}
                      isChecked={isChecked}
                      to={path}
                      locale={lang}
                      isActive={isChecked}
                    >
                      <Styled.languageFlag
                        role="img"
                        aria-label={AVAILABLE_LANGUAGES[lang]}
                      >
                        {AVAILABLE_FLAGS[lang]}
                      </Styled.languageFlag>
                      <Styled.languageLanguage isChecked={isChecked}>
                        {AVAILABLE_LANGUAGES[lang]}
                      </Styled.languageLanguage>
                      <Styled.languageCheck
                        name="checkmark-16"
                        fill={isChecked ? COLORS.primary : 'none'}
                        width={16}
                        height={16}
                      />
                    </Styled.language>
                    {index < supportedLanguages.length - 1 && (
                      <Styled.separator />
                    )}
                  </Fragment>
                )
              })}
            </Styled.tooltipContent>
          </Styled.tootipContainer>
        </Styled.tooltip>
      </Styled>
    </ThemeProvider>
  )
}

LocaleSwitcher.defaultProps = {
  getConfig: () => {},
  variant: 'footer',
  path: '/',
}

export default LocaleSwitcher
